import { Button, makeStyles, shorthands } from "@fluentui/react-components";
import { Delete20Regular, Add20Regular } from "@fluentui/react-icons";
import * as React from "react";
import {
  checkEntrySum,
  entryPath,
  PERSON_ENTRY_FORMATS,
  subPath,
} from "@kmvs/forms-core";
import { BRAND, REFINED } from "./tokens";
import EntryField from "./EntryField";
import KontaktdatenGroup from "./KontaktdatenGroup";
import type { FieldDef, PersonEntry, Kontaktdaten } from "./types";

const useStyles = makeStyles({
  description: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    marginBottom: "12px",
  },
  card: {
    backgroundColor: "#faf8fb",
    ...shorthands.padding("16px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    marginBottom: "12px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
    color: BRAND.ink,
  },
  topGrid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  fieldLabel: {
    display: "block",
    minWidth: 0,
  },
  fieldLabelText: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "3px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  sectionLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  addButton: {
    alignSelf: "flex-start",
  },
  /**
   * Die Anteilssumme ist eine Aussage über die GANZE Liste und kann keinem
   * einzelnen Eintrag zugeordnet werden — sie steht deshalb einmal unter den
   * Karten (die Formatmeldungen dagegen an ihrem Eintrag). Gold statt Rot:
   * sie merkt an, sie blockiert nicht.
   */
  summe: {
    display: "block",
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: REFINED.goldInk,
    backgroundColor: REFINED.goldBg,
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", REFINED.goldLine),
  },
});

const makeId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pe-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

const emptyEntry = (withAnteile: boolean): PersonEntry => ({
  id: makeId(),
  vorname: "",
  nachname: "",
  geburtsdatum: "",
  funktion: "",
  kontakt: {},
  ...(withAnteile ? { anteile: "" } : {}),
});

type Props = {
  field: FieldDef;
  value: PersonEntry[];
  onChange: (next: PersonEntry[]) => void;
};

const PersonEntryList = ({ field, value, onChange }: Props) => {
  const styles = useStyles();
  const withAnteile = !!field.withAnteile;
  const options = field.functionOptions ?? [];

  /**
   * Ist die Liste leer, zeigen wir einen leeren Eintrag vor, damit die Felder
   * sofort sichtbar sind — aber **einmal erzeugt**, nicht bei jedem Render.
   *
   * `emptyEntry` würfelt eine frische `crypto.randomUUID()`, und die Karte ist
   * mit `key={entry.id}` gekeyt. Ein neuer Platzhalter pro Render hiesse also:
   * bei JEDEM Render ein Unmount samt Remount der Karte. Sobald diese
   * Komponente selbst rendert — und das tut sie seit dem Fokuszustand
   * `anteilFokus` schon beim ersten Klick ins Anteilsfeld — wäre das gerade
   * fokussierte `<input>` weg: Fokus verloren, getippte Zeichen im Nichts.
   * Genau das ist der Zustand jeder frischen Analyse, denn QuestionField
   * reicht bei fehlender Antwort `[]` durch.
   *
   * `value` darf NICHT in die Abhängigkeiten: QuestionField baut dieses leere
   * Array bei jedem Render neu, die Abhängigkeit wäre wirkungslos.
   */
  const platzhalter = React.useMemo(
    () => emptyEntry(withAnteile),
    [withAnteile],
  );
  const entries = value.length > 0 ? value : [platzhalter];

  /**
   * Solange in einem Anteilsfeld getippt wird, schweigt die Summe: sonst
   * erschiene die Warnung nach der ersten Ziffer von "50" und verschwände
   * nach der zweiten. Nach dem Verlassen — und beim bloßen Öffnen eines
   * Bestandsdatensatzes, in dem niemand etwas anfasst — steht sie da.
   */
  const [anteilFokus, setAnteilFokus] = React.useState(false);
  const summe = withAnteile ? checkEntrySum(field.key, entries) : undefined;
  const summeId = `${field.key}-anteile-summe`;
  const summeSichtbar = !!summe && !anteilFokus;

  const update = (id: string, patch: Partial<PersonEntry>) => {
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };
  const updateKontakt = (id: string, kontakt: Kontaktdaten) => {
    onChange(entries.map((e) => (e.id === id ? { ...e, kontakt } : e)));
  };
  const remove = (id: string) => {
    const next = entries.filter((e) => e.id !== id);
    onChange(next.length > 0 ? next : [emptyEntry(withAnteile)]);
  };
  const add = () => {
    onChange([...entries, emptyEntry(withAnteile)]);
  };

  return (
    <div>
      {field.description && (
        <span className={styles.description}>{field.description}</span>
      )}
      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardTitle}>Eintrag {idx + 1}</span>
            {entries.length > 1 && (
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                onClick={() => remove(entry.id)}
                aria-label="Eintrag entfernen"
              >
                Entfernen
              </Button>
            )}
          </div>
          <div className={styles.topGrid}>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldLabelText}>Vorname</span>
              <input
                type="text"
                className={styles.input}
                value={entry.vorname}
                onChange={(e) => update(entry.id, { vorname: e.target.value })}
              />
            </label>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldLabelText}>Nachname / Firma</span>
              <input
                type="text"
                className={styles.input}
                value={entry.nachname}
                onChange={(e) => update(entry.id, { nachname: e.target.value })}
              />
            </label>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldLabelText}>Geburtsdatum</span>
              <input
                type="date"
                className={styles.input}
                value={entry.geburtsdatum}
                onChange={(e) => update(entry.id, { geburtsdatum: e.target.value })}
              />
            </label>
            <label className={styles.fieldLabel}>
              <span className={styles.fieldLabelText}>Funktion</span>
              <select
                className={styles.input}
                value={entry.funktion}
                onChange={(e) => update(entry.id, { funktion: e.target.value })}
              >
                <option value="">— wählen —</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {withAnteile && (
              <EntryField
                label="Anteile (%)"
                listKey={field.key}
                pfad={subPath(entryPath(field.key, entry, idx), "anteile")}
                format={PERSON_ENTRY_FORMATS.anteile}
                value={entry.anteile ?? ""}
                onChange={(next) => update(entry.id, { anteile: next })}
                onFokusWechsel={setAnteilFokus}
                describedBy={summeSichtbar ? summeId : undefined}
              />
            )}
          </div>
          <div>
            <span className={styles.sectionLabel}>Kontaktdaten</span>
            <div style={{ marginTop: 6 }}>
              <KontaktdatenGroup
                value={entry.kontakt}
                onChange={(k) => updateKontakt(entry.id, k)}
                listKey={field.key}
                pfad={subPath(entryPath(field.key, entry, idx), "kontakt")}
              />
            </div>
          </div>
        </div>
      ))}
      {summeSichtbar && summe && (
        // Bewusst ohne role="alert" und ohne aria-invalid an den Feldern: die
        // Summe wird angemerkt, nie beanstandet — der Bestand enthält einen
        // Datensatz mit 152 %, und "ca."-Verteilungen sind fachlich gewollt.
        <span id={summeId} className={styles.summe}>
          {summe.message}
        </span>
      )}
      <Button
        appearance="primary"
        icon={<Add20Regular />}
        onClick={add}
        className={styles.addButton}
      >
        Weiteren Eintrag hinzufügen
      </Button>
    </div>
  );
};

export default PersonEntryList;
